import { useState, type FormEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormLabel,
  MenuItem,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ScienceRounded from '@mui/icons-material/ScienceRounded';
import { ApiError } from '@/api';
import { useAuth } from '@/auth';
import { FormField } from '@/components/form/FormField';
import { useAsync } from '@/hooks/useAsync';
import { AuthLayout } from '@/layouts/AuthLayout';
import { AuthHeader } from '@/pages/auth/AuthHeader';
import { getSignupOptions, setTestProfile } from './api';
import { useSession } from './session';
type TestRole = 'student' | 'teacher' | 'coordinator';

/**
 * Test accounts (TEST_EMAILS) on each sign-in: enter as a student (institution + course), a
 * teacher or a coordinator, to see every side of the platform. No institutional e-mail, no
 * approval.
 */
export function TestProfilePage({ onChosen }: { onChosen: () => void }) {
  const { signOut } = useAuth();
  const { user, refresh } = useSession();
  const options = useAsync(getSignupOptions);
  const [role, setRole] = useState<TestRole>(
    user.role === 'teacher' || user.role === 'coordinator' ? user.role : 'student',
  );
  const [institutionId, setInstitutionId] = useState(user.institutions[0]?.id ?? '');
  const [courseId, setCourseId] = useState(user.course?.id ?? '');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setPending(true);
    setError(null);
    try {
      await setTestProfile({
        role,
        institutionId,
        courseId: role === 'student' ? courseId : null,
      });
      await refresh();
      onChosen();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível entrar. Tente de novo.');
    } finally {
      setPending(false);
    }
  };

  const ready = Boolean(institutionId) && (role !== 'student' || Boolean(courseId));

  return (
    <AuthLayout>
      <AuthHeader
        title="Como você quer entrar?"
        subtitle="Conta de teste: escolha o perfil desta vez. Dá para trocar no menu da sua foto."
      />
      <Alert severity="info" icon={<ScienceRounded />} sx={{ mb: 3 }}>
        Sem e-mail institucional e sem aprovação. Suas tentativas e notas continuam quando você
        troca de perfil.
      </Alert>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {options.loading ? (
        <Stack spacing={2}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="rounded" height={56} />
          ))}
        </Stack>
      ) : (
        <Box component="form" noValidate onSubmit={submit}>
          <Stack spacing={2.5}>
            <Stack spacing={1}>
              <FormLabel id="test-role">Entrar como</FormLabel>
              <ToggleButtonGroup
                exclusive
                fullWidth
                color="primary"
                value={role}
                onChange={(_, next: TestRole | null) => next && setRole(next)}
                aria-labelledby="test-role"
              >
                <ToggleButton value="student">Estudante</ToggleButton>
                <ToggleButton value="teacher">Professor(a)</ToggleButton>
                <ToggleButton value="coordinator">Coordenação</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
            <FormField
              select
              label="Instituição"
              value={institutionId}
              onChange={(e) => setInstitutionId(e.target.value)}
            >
              {(options.data?.institutions ?? []).map((i) => (
                <MenuItem key={i.id} value={i.id}>
                  {i.name}
                </MenuItem>
              ))}
            </FormField>
            {role === 'student' && (
              <FormField
                select
                label="Curso"
                value={courseId}
                onChange={(e) => setCourseId(e.target.value)}
              >
                {(options.data?.courses ?? []).map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </FormField>
            )}
            <Button type="submit" variant="contained" size="large" disabled={pending || !ready}>
              {pending ? <CircularProgress size={22} color="inherit" /> : 'Entrar'}
            </Button>
          </Stack>
        </Box>
      )}
      <Typography variant="body2" sx={{ textAlign: 'center', mt: 3 }}>
        <Button size="small" color="inherit" onClick={() => void signOut()}>
          Sair
        </Button>
      </Typography>
    </AuthLayout>
  );
}
